// P4-SCHED Phase 5 — historical comparables (SCHEDULE_INTELLIGENCE_MODULE.md
// §6, §23, §25, §54–§56, §61). Pure functions, no React, no DB.
//
// The dataset (estimating_historical_correlation_analysis_2024_2026.xlsx,
// Normalized_Data sheet) is the product owner's normalized read of past
// ESTIMATES: per estimate a scope class, physical quantities, project value,
// data-quality flags and the estimator's construction duration in weeks.
// Two facts govern everything here:
//   1. `construction_duration_wks` is what an estimator BELIEVED, not how long
//      the work took (§56) — every row carries DurationKind 'estimated'; a
//      future actuals import uses 'actual' and is never mixed in silently.
//   2. It holds NO production rates. Nothing here produces a duration. It
//      VALIDATES a generated schedule against comparable history — a second
//      opinion with a sample size, never the formula (§6, §25).
//
// The workbook names clients and dollar values, so it is never committed.
// The admin pastes the sheet (Excel copy → tab-separated) or uploads a CSV
// export; parseHistoricalTable() maps the headers by alias and the rows go to
// ScheduleHistoricalEstimates (db/78) through sp_ImportScheduleHistoricalEstimates.

import { calendarFromHeader } from "./scheduleEngine";

// Normalized field → accepted header aliases (case / space / underscore
// insensitive). Unknown headers are reported, never guessed.
export const HISTORY_FIELDS = [
  { key: "cohort_year",               type: "int",   aliases: ["cohort_year", "cohort", "year"] },
  { key: "file",                      type: "text",  aliases: ["file", "source_file", "workbook"] },
  { key: "opportunity_name",          type: "text",  aliases: ["opportunity_name", "opportunity", "project", "project_name"] },
  { key: "scope_class",               type: "text",  aliases: ["scope_class", "scope", "class"] },
  { key: "usable_standard",           type: "bool",  aliases: ["usable_standard", "usable", "standardized"] },
  { key: "data_quality_flags",        type: "text",  aliases: ["data_quality_flags", "quality_flags", "flags"] },
  { key: "project_sf",                type: "num",   aliases: ["project_sf", "sf", "project_square_feet"] },
  { key: "project_total_value",       type: "num",   aliases: ["project_total_value", "total_value", "value", "project_value"] },
  { key: "construction_duration_wks", type: "num",   aliases: ["construction_duration_wks", "duration_wks", "duration_weeks", "construction_weeks", "weeks"] },
  { key: "cost_per_sf",               type: "num",   aliases: ["cost_per_sf", "$/sf", "cost_sf"] },
  { key: "ceiling_sf",                type: "num",   aliases: ["ceiling_sf"] },
  { key: "two_in_wall_sf",            type: "num",   aliases: ["two_in_wall_sf", "2in_wall_sf", "2_in_wall_sf"] },
  { key: "half_in_wall_sf",           type: "num",   aliases: ["half_in_wall_sf", "0.5in_wall_sf", "half_wall_sf"] },
  { key: "wall_sf",                   type: "num",   aliases: ["wall_sf"] },
  { key: "single_doors",              type: "num",   aliases: ["single_doors", "doors_single"] },
  { key: "double_doors",              type: "num",   aliases: ["double_doors", "doors_double"] },
  { key: "windows",                   type: "num",   aliases: ["windows", "window_count"] },
  { key: "opening_count",             type: "num",   aliases: ["opening_count", "openings"] },
  { key: "cove_lf",                   type: "num",   aliases: ["cove_lf", "cove"] },
  { key: "hepa_count",                type: "num",   aliases: ["hepa_count", "hepas", "hepa"] },
  { key: "light_count",               type: "num",   aliases: ["light_count", "lights", "lighting_count"] },
  { key: "envelope_sf",               type: "num",   aliases: ["envelope_sf"] },
  { key: "device_count",              type: "num",   aliases: ["device_count", "devices"] },
  { key: "quantity_intensity",        type: "num",   aliases: ["quantity_intensity", "intensity"] },
];
const norm = (h) => String(h ?? "").trim().toLowerCase().replace(/[\s\-]+/g, "_").replace(/[^a-z0-9_$/.]/g, "");
const FIELD_BY_ALIAS = new Map();
for (const f of HISTORY_FIELDS) for (const a of f.aliases) FIELD_BY_ALIAS.set(norm(a), f);

// Dataset scope classes ↔ the schedule profile's delivery models.
export const SCOPE_CLASS_TO_DELIVERY_MODEL = {
  CORE_ENVELOPE: "core_envelope", TURNKEY_FULL_SCOPE: "turnkey_full_scope", TURNKEY_OR_COMPLEX: "turnkey_full_scope", MEP_HEAVY: "mep_heavy",
  MATERIAL_PARTIAL: "material_only", MATERIAL_ONLY: "material_only", LABOR_INSTALL_ONLY: "labor_install_only", DESIGN_ONLY: "design_only",
  COLD_ROOM_SPECIAL: "cold_room", COLD_ROOM: "cold_room", ALTERATION_CHANGE: "change_order", CHANGE_ORDER: "change_order", SHUTDOWN: "shutdown",
  PHASED_OCCUPIED: "phased_occupied", CONCEPTUAL_FULL_FACILITY: "conceptual_full_facility",
};
export const scopeClassesForDeliveryModel = (model) => Object.entries(SCOPE_CLASS_TO_DELIVERY_MODEL).filter(([, m]) => m === model).map(([c]) => c);

// ── Parsing ───────────────────────────────────────────────────────────────────
// Splits one delimited line honouring double-quoted fields (Excel CSV rules).
export function splitDelimited(line, delim) {
  const out = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) { if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; }
    else if (ch === '"') q = true;
    else if (ch === delim) { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out;
}
const detectDelimiter = (headerLine) => (headerLine.includes("\t") ? "\t" : headerLine.split(",").length > headerLine.split(";").length ? "," : ";");
const toBool = (v) => { const s = String(v ?? "").trim().toLowerCase(); return s === "" ? null : ["true", "1", "yes", "y", "t"].includes(s); };
const toNum = (v) => { if (v == null) return null; const s = String(v).replace(/[$,\s]/g, ""); if (s === "" || s === "-") return null; const n = +s; return Number.isFinite(n) ? n : NaN; };

// text → { rows, columns: { mapped: [{ header, key }], unmapped: [header] }, errors: [{ line, message }], delimiter }
// A row needs a cohort year and a file (the natural key); anything else may
// be blank. A non-numeric value in a numeric column is an error on that row,
// never silently zeroed.
export function parseHistoricalTable(text) {
  const lines = String(text ?? "").replace(/\r\n?/g, "\n").split("\n").filter(l => l.trim() !== "");
  if (lines.length < 2) return { rows: [], columns: { mapped: [], unmapped: [] }, errors: [{ line: 0, message: "Paste the sheet with its header row and at least one data row." }], delimiter: null };
  const delim = detectDelimiter(lines[0]);
  const headers = splitDelimited(lines[0], delim).map(h => h.replace(/^﻿/, ""));
  const mapped = [], unmapped = [];
  const fieldAt = headers.map(h => { const f = FIELD_BY_ALIAS.get(norm(h)) || null; (f ? mapped : unmapped).push(f ? { header: h, key: f.key } : h); return f; });
  const errors = [];
  if (!fieldAt.some(f => f?.key === "cohort_year")) errors.push({ line: 1, message: "No cohort_year column found." });
  if (!fieldAt.some(f => f?.key === "file")) errors.push({ line: 1, message: "No file column found (the natural key of a row)." });
  if (!fieldAt.some(f => f?.key === "construction_duration_wks")) errors.push({ line: 1, message: "No construction_duration_wks column found." });
  const rows = [];
  if (errors.length) return { rows, columns: { mapped, unmapped }, errors, delimiter: delim };
  for (let i = 1; i < lines.length; i++) {
    const cells = splitDelimited(lines[i], delim);
    const row = {}; let bad = null;
    fieldAt.forEach((f, c) => {
      if (!f) return;
      const raw = cells[c];
      if (f.type === "num" || f.type === "int") { const n = toNum(raw); if (Number.isNaN(n)) bad = bad || `"${raw}" is not a number in ${f.key}`; row[f.key] = Number.isNaN(n) ? null : (f.type === "int" && n != null ? Math.round(n) : n); }
      else if (f.type === "bool") row[f.key] = toBool(raw);
      else row[f.key] = raw == null || String(raw).trim() === "" ? null : String(raw).trim();
    });
    if (bad) { errors.push({ line: i + 1, message: bad }); continue; }
    if (row.cohort_year == null || !row.file) { errors.push({ line: i + 1, message: "cohort_year and file are required" }); continue; }
    if (row.usable_standard == null) row.usable_standard = false;
    rows.push(row);
  }
  return { rows, columns: { mapped, unmapped }, errors, delimiter: delim };
}

// Row → sp_ImportScheduleHistoricalEstimates payload row.
export const historyRowPayload = (r) => ({ ...r, duration_kind: r.duration_kind || "estimated" });

// ── The current estimate's features (same vocabulary as the dataset) ─────────
// From the generator's takeoff (scheduleActivityGenerator.generate…().takeoff)
// and the rooms. quantity_intensity is NOT recomputed — the dataset's formula
// is not documented, so it is never compared. Value is optional (the caller
// may pass the estimate's total when it has one).
export function currentEstimateFeatures({ takeoff, rooms = [], projectValue = null }) {
  const floor = rooms.reduce((s, r) => s + ((+r.LengthFt > 0 && +r.WidthFt > 0) ? (+r.LengthFt * +r.WidthFt) : 0), 0);
  const by = takeoff?.elements?.byBasis || {};
  const o = takeoff?.openings || {};
  const wallSf = takeoff?.walls?.sf ?? 0;
  const ceilingSf = by.ceiling_area?.qty ?? 0;
  return {
    project_sf: Math.round(floor), project_total_value: projectValue,
    wall_sf: wallSf, ceiling_sf: ceilingSf, envelope_sf: Math.round(wallSf + ceilingSf),
    opening_count: (o.door?.count ?? 0) + (o.window?.count ?? 0) + (o.pass_through?.count ?? 0),
    single_doors: o.door?.count ?? 0, windows: o.window?.count ?? 0,
    hepa_count: by.engine_hepa?.qty ?? 0, light_count: by.engine_light?.qty ?? 0, cove_lf: by.perimeter?.qty ?? 0,
  };
}

// ── Comparables (§23 hierarchical matching) ───────────────────────────────────
export function percentile(sorted, p) {
  if (!sorted.length) return null;
  const i = (sorted.length - 1) * p, lo = Math.floor(i), hi = Math.ceil(i);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
}
export function durationStats(rows) {
  const d = rows.map(r => +r.construction_duration_wks).filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b);
  if (!d.length) return { n: 0 };
  return { n: d.length, min: d[0], p25: percentile(d, 0.25), median: percentile(d, 0.5), p75: percentile(d, 0.75), max: d[d.length - 1] };
}
const within = (a, b, tol) => a != null && b != null && a > 0 && b > 0 && Math.abs(a - b) / Math.max(a, b) <= tol;

// Comparable rows for the current estimate, from the most specific cohort with
// enough members down to "every usable estimate". Only usable, estimated,
// positive-duration rows ever qualify. Returns { level, levelLabel, rows,
// stats, nearest, considered }.
export const COMPARABLE_LEVELS = [
  { level: 1, label: "Same scope class · similar project SF · similar envelope SF" },
  { level: 2, label: "Same scope class · similar project SF" },
  { level: 3, label: "Same scope class" },
  { level: 4, label: "All usable estimates" },
];
export function findComparables(features, history = [], { deliveryModel = null, minN = 5, sizeTol = 0.5, durationKind = "estimated" } = {}) {
  const pool = history.filter(r => (r.usable_standard === true || r.usable_standard === 1) && (+r.construction_duration_wks) > 0 && (r.duration_kind || "estimated") === durationKind);
  const classes = new Set(deliveryModel ? scopeClassesForDeliveryModel(deliveryModel) : []);
  const sameClass = (r) => classes.size === 0 || classes.has(String(r.scope_class || "").toUpperCase());
  const tiers = [
    pool.filter(r => sameClass(r) && within(r.project_sf, features.project_sf, sizeTol) && within(r.envelope_sf, features.envelope_sf, sizeTol)),
    pool.filter(r => sameClass(r) && within(r.project_sf, features.project_sf, sizeTol)),
    pool.filter(r => sameClass(r)),
    pool,
  ];
  let level = 0, rows = [];
  for (let i = 0; i < tiers.length; i++) if (tiers[i].length >= minN) { level = i + 1; rows = tiers[i]; break; }
  const nearest = [...pool].map(r => ({ row: r, distance: featureDistance(features, r) })).filter(x => Number.isFinite(x.distance)).sort((a, b) => a.distance - b.distance).slice(0, 5);
  return { level, levelLabel: level ? COMPARABLE_LEVELS[level - 1].label : "Insufficient history", rows, stats: durationStats(rows), nearest, considered: pool.length, tierCounts: tiers.map(t => t.length) };
}
// Normalized distance over the physical features both sides have (log ratio,
// so 2× and ½ are equally far) — for the "nearest projects" list only.
export function featureDistance(a, b) {
  const keys = ["project_sf", "envelope_sf", "wall_sf", "ceiling_sf", "opening_count", "hepa_count", "light_count", "cove_lf"];
  let s = 0, n = 0;
  for (const k of keys) {
    const x = +a?.[k], y = +b?.[k];
    if (!(x > 0) || !(y > 0)) continue;
    const d = Math.log(x / y); s += d * d; n++;
  }
  return n ? Math.sqrt(s / n) : NaN;
}

// ── Validation (§25, §61) ─────────────────────────────────────────────────────
export const VALIDATION_STATUS = {
  INSUFFICIENT_HISTORY: "INSUFFICIENT_HISTORY", AGGRESSIVE: "AGGRESSIVE", WITHIN_EXPECTED_RANGE: "WITHIN_EXPECTED_RANGE", CONSERVATIVE: "CONSERVATIVE", NO_SCHEDULE: "NO_SCHEDULE",
};
// Likely causes offered for review when the generated schedule is shorter than
// history — hints for the estimator to check, never conclusions.
export const AGGRESSIVE_HINTS = [
  "Activities not yet generated (MEP, controls, commissioning are Release 2)",
  "Production standards higher than CleanSpace's real rates",
  "Parallel crews or shift pattern more aggressive than the job will get",
  "Procurement lead times not in the network yet",
  "No shutdown / occupied-facility constraint applied",
];
export function validateAgainstHistory({ generatedWeeks, stats, minN = 5 }) {
  if (generatedWeeks == null || !Number.isFinite(generatedWeeks)) return { status: VALIDATION_STATUS.NO_SCHEDULE };
  if (!stats || (stats.n ?? 0) < minN) return { status: VALIDATION_STATUS.INSUFFICIENT_HISTORY, generatedWeeks, n: stats?.n ?? 0 };
  let status = VALIDATION_STATUS.WITHIN_EXPECTED_RANGE;
  if (generatedWeeks < stats.p25) status = VALIDATION_STATUS.AGGRESSIVE;
  else if (generatedWeeks > stats.p75) status = VALIDATION_STATUS.CONSERVATIVE;
  return {
    status, generatedWeeks, n: stats.n, p25: stats.p25, median: stats.median, p75: stats.p75,
    deltaToMedianWeeks: generatedWeeks - stats.median,
    deltaToMedianPct: stats.median > 0 ? (generatedWeeks - stats.median) / stats.median * 100 : null,
    hints: status === VALIDATION_STATUS.AGGRESSIVE ? AGGRESSIVE_HINTS : [],
  };
}

// The generated CONSTRUCTION span in weeks — the quantity the dataset's
// construction_duration_wks describes (field work: first construction-phase
// start through project finish), not design + procurement. Working days ÷
// the schedule's work days per week.
const FIELD_PHASES = new Set(["construction", "startup", "commissioning", "closeout"]);
export function constructionSpan(evaluated) {
  if (!evaluated || evaluated.network?.cycles) return { days: null, weeks: null, startDay: null, endDay: null };
  const field = evaluated.activities.filter(a => a.cpm && FIELD_PHASES.has(a.Phase) && !a.eff.required && (a.eff.effective > 0 || a.IsMilestone));
  if (!field.length) return { days: null, weeks: null, startDay: null, endDay: null };
  const startDay = Math.min(...field.map(a => a.cpm.ES));
  const endDay = Math.max(...field.map(a => a.cpm.EF));
  const cal = evaluated.calendar || calendarFromHeader({});
  const days = Math.max(0, endDay - startDay);
  return { days, weeks: days / (cal.workDaysPerWeek || 5), startDay, endDay };
}
